import Icon from './Icon.jsx'

/*
 * Buttons — DESIGN.md §6.
 *  - primary:   #6D28D9 bg, white, shadow.
 *  - secondary: white bg, 1.5px #ece6f7 border, ink text.
 *  - success:   #15803D. danger: #DC2626.
 * Labelled buttons ALWAYS carry a leading icon (icon prop).
 * For repeated table-row actions use IconButton (32px square, no label).
 */
const VARIANTS = {
  primary: {
    background: 'var(--color-primary)', color: '#fff',
    border: '1.5px solid transparent', boxShadow: 'var(--shadow-primary)',
  },
  secondary: {
    background: '#fff', color: 'var(--color-ink)',
    border: '1.5px solid var(--color-border-alt)',
  },
  success: {
    background: 'var(--color-success)', color: '#fff', border: '1.5px solid transparent',
  },
  danger: {
    background: 'var(--color-danger)', color: '#fff', border: '1.5px solid transparent',
  },
  'danger-outline': {
    background: '#fff', color: 'var(--color-danger)',
    border: '1.5px solid var(--color-danger)',
  },
  ghost: {
    background: 'transparent', color: 'var(--color-text)', border: '1.5px solid transparent',
  },
}

export default function Button({
  variant = 'primary',
  icon,
  iconSize = 17,
  children,
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  style,
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.primary
  return (
    <button
      type={type}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        height: 44, padding: '0 18px', borderRadius: 'var(--radius-field)',
        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
        width: fullWidth ? '100%' : undefined,
        opacity: disabled || loading ? 0.6 : 1,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'filter .15s ease, transform .05s ease',
        ...v, ...style,
      }}
      {...rest}
    >
      {icon && <Icon name={loading ? 'settings' : icon} size={iconSize} />}
      {children}
    </button>
  )
}

export function IconButton({ icon, title, variant = 'default', size = 32, onClick, disabled, style, ...rest }) {
  // `success` is a filled green square (no border) — used for "mark as paid"
  // in the invoice list, matching the Claude Design handoff.
  // Colour code (handoff): neutral grey for view/copy, violet for edit, red for
  // delete — each with a subtle matching tint so the action reads at a glance.
  const colors = {
    default: { color: 'var(--color-text-muted)', border: 'var(--color-border-alt)', bg: '#fff' },
    danger: { color: 'var(--color-danger)', border: '#f3c9c9', bg: 'var(--color-danger-bg)' },
    primary: { color: 'var(--color-primary)', border: '#e3dbf5', bg: 'var(--color-bg-lilac)' },
    success: { color: 'var(--color-success)', border: 'transparent', bg: 'var(--color-success-bg)' },
  }[variant] || { color: 'var(--color-text-muted)', border: 'var(--color-border-alt)', bg: '#fff' }
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size, height: size, borderRadius: 'var(--radius-icon-btn)',
        background: colors.bg, border: `1.5px solid ${colors.border}`, color: colors.color,
        opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background .15s ease',
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} size={17} />
    </button>
  )
}
